import React, { useState } from "react";
// Import Material-UI components for navigation, layout, and icons
import {
  AppBar,
  Box,
  Container,
  IconButton,
  Link,
  Toolbar,
  Grid,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  MenuItem,
  Menu,
} from "@mui/material";
// Import website configuration for images
import { websiteImages } from "../../../config/config";
// Import icons for navigation and user actions
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LoginIcon from "@mui/icons-material/Login";
// Import custom authentication popup component
import AuthPopup from "../../../components/AuthPopup"; 
// Import Redux hooks for state management
import { useDispatch, useSelector } from "react-redux";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Logout } from "@mui/icons-material";
// Import custom logout popup component
import LogoutPopup from "../../../components/ClientLogoutPopup";
import "./Navbar.css";
// Import custom change password dialog component
import ChangePasswordDialog from "../../../components/ChangePasswordDialog";
// Import hook for programmatic navigation
import { useNavigate } from "react-router-dom";

// Define the Navbar component
const Navbar = () => {
  // State to manage mobile drawer visibility
  const [mobileOpen, setMobileOpen] = useState(false);
  // State to manage login popup visibility
  const [loginOpen, setLoginOpen] = useState(false);
  // State to manage user menu dropdown
  const [anchorEl, setAnchorEl] = useState(null);
  const isMenuOpen = Boolean(anchorEl);
  // Access user data from Redux store
  const user = useSelector((state) => state.auth.user); // Adjust path if needed
  const dispatch = useDispatch();
  // State to manage logout popup visibility
  const [logoutpopupOpen, setLogoutpopupOpen] = useState(false);
  // State to manage change password dialog visibility
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  // Hook for navigation
  const navigate = useNavigate();

  // Handle user menu click to open dropdown
  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Close user menu dropdown
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Toggle mobile drawer visibility
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Define mobile drawer content
  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{ textAlign: "center", backgroundColor: "#000", height: "100%" }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        {/* Logo in mobile drawer */}
        <Box
          com
ponent="img"
          src={websiteImages.websiteLogo}
          alt="RECOPM+"
          sx={{ height: "30px", width: "130px" }}
        />
        {/* Close button for mobile drawer */}
        <IconButton color="inherit" sx={{ color: "white" }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {[
          "12–Week Programs",
          "The Bikini Method",
          "Premium Coaching",
          "Education",
          "Contact",
        ].map((item, i) => (
          <ListItem button key={i}>
            <ListItemText>
              <Link
                href="#"
                variant="body2"
                sx={{
                  color: "white",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {item}
                {/* Conditionally render arrow icon for specific menu items */}
                {["Premium Coaching", "Contact"].includes(item) ? null : (
                  <ArrowForwardIcon
                    fontSize="small"
                    sx={{ color: "#00ffff" }}
                  />
                )}
              </Link>
            </ListItemText>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <div>
        {/* Main navigation container */}
        <Box
          className="nav-container"
          sx={{
            backgroundColor: "#111111",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "center",
            px: { xs: 2, md: 3 },
            pb: { xs: 8, md: 10 },
          }}
        >
          {/* AppBar for navigation */}
          <AppBar
            position="absolute"
            sx={{
              backgroundColor: "transparent",
              boxShadow: "none",
              top: { xs: "20px", md: "34px" },
              left: "0px",
            }}
            className="container"
          >
            <Toolbar
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: { xs: 2, md: 3 },
              }}
            >
              {/* Logo */}
              <Link href="/">
                <Box
                  component="img"
                  src={websiteImages.websiteLogo}
                  alt="RECOPM+"
                  sx={{ height: "30px", width: "130px" }}
                />
              </Link>

              {/* Desktop navigation menu */}
              <Box
                sx={{ display: { xs: "none", md: "flex" } }}
                className="nav-menu"
              >
                {[
                  "12–Week Programs",
                  "The Bikini Method",
                  "Premium Coaching",
                  "Education",
                  "Contact",
                ].map((item, i) => (
                  <Link
                    key={i}
                    href="#"
                    variant="body2"
                    sx={{
                      color: "white",
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      ml: i === 0 ? 0 : 2,
                    }}
                  >
                    {item}
                    {/* Conditionally render arrow icon for specific menu items */}
                    {["Premium Coaching", "Contact"].includes(item) ? null : (
                      <ArrowForwardIcon
                        fontSize="small"
                        sx={{ color: "#00ffff" }}
                      />
                    )}
                  </Link>
                ))}
              </Box>

              {/* Icons and mobile menu toggle */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {/* Conditional rendering for authenticated/unauthenticated users */}
                {user ? (
                  <>
                    {/* Desktop welcome button */}
                    <Button
                      onClick={handleMenuClick}
                      sx={{
                        display: { xs: "none", md: "flex" },
                        color: "white",
                        border: "1px solid #00ffff",
                        textTransform: "none",
                        ml: 2,
                        fontWeight: 500,
                        "&:hover": {
                          backgroundColor: "#00ffff",
                          color: "#000",
                        },
                      }}
                    >
                      Welcome, {user.name}
                    </Button>

                    {/* Mobile user icon */}
                    <IconButton
                      onClick={handleMenuClick}
                      color="inherit"
                      sx={{
                        display: { xs: "flex", md: "none" },
                        ml: 1,
                        color: "white",
                      }}
                    >
                      <AccountCircleIcon />
                    </IconButton>

                    {/* User dropdown menu */}
                    <Menu
                      anchorEl={anchorEl}
                      open={isMenuOpen}
                      onClose={handleMenuClose}
                      disableScrollLock
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      PaperProps={{
                        sx: {
                          backgroundColor: "#ffffffff",
                          color: "#000000ff",
                          borderRadius: 1,
                          margin: "14px 0 0 0",
                          padding: "0px",
                        },
                      }}
                    >
                      {/* Profile menu item */}
                      <MenuItem
                        onClick={() => {
                          handleMenuClose();
                          navigate("/profile");
                        }}
                        component="a"
                        sx={{
                          borderBottom: "1px dotted #030303ff",
                          "&:hover": {
                            backgroundColor: "#00ffff",
                            color: "#000",
                          },
                        }}
                      >
                        Profile
                      </MenuItem>
                      {/* Change password menu item */}
                      <MenuItem
                        onClick={handleMenuClose}
                        sx={{
                          borderBottom: "1px dotted #030303ff",
                          "&:hover": {
                            backgroundColor: "#00ffff",
                            color: "#000",
                          },
                        }}
                      >
                        Change Password
                      </MenuItem>
                      {/* Logout menu item */}
                      <MenuItem
                        onClick={() => {
                          handleMenuClose();
                          setLogoutpopupOpen(true);
                        }}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#00ffff",
                            color: "#000",
                          },
                        }}
                      >
                        Logout
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <>
                    {/* Desktop login/signup button */}
                    <Button
                      onClick={() => setLoginOpen(true)}
                      startIcon={<LoginIcon />}
                      sx={{
                        display: { xs: "none", md: "flex" },
                        color: "white",
                        border: "1px solid #00ffff",
                        textTransform: "none",
                        ml: 2,
                        fontWeight: 500,
                        "&:hover": {
                          backgroundColor: "#00ffff",
                          color: "#000",
                        },
                      }}
                    >
                      Login / Sign Up
                    </Button>
                    {/* Mobile login icon */}
                    <IconButton
                      onClick={() => setLoginOpen(true)}
                      color="inherit"
                      sx={{ display: { xs: "flex", md: "none" }, ml: 1 }}
                    >
                      <LoginIcon />
                    </IconButton>
                  </>
                )}

                {/* Mobile menu toggle button */}
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ display: { md: "none" } }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Toolbar>
          </AppBar>

          {/* Mobile drawer */}
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: "block", md: "none" },
              "& .MuiDrawer-paper": {
                boxSizing: "border-box",
                width: 280,
                backgroundColor: "#000",
              },
            }}
          >
            {drawer}
          </Drawer>

          {/* Authentication popup */}
          <AuthPopup open={loginOpen} onClose={() => setLoginOpen(false)} />
          {/* Logout popup */}
          <LogoutPopup
            open={logoutpopupOpen}
            onClose={() => setLogoutpopupOpen(false)}
          />
          {/* Change password dialog */}
          <ChangePasswordDialog
            open={changePasswordOpen}
            onClose={() => setChangePasswordOpen(false)}
          />
        </Box>
      </div>
    </>
  );
};

// Export the Navbar component
export default Navbar;